/**
 * Recipe Parser Utility
 *
 * Parses Markdown recipe files with YAML frontmatter into structured data.
 * Uses the 'yaml' package for YAML parsing and 'marked' for Markdown-to-HTML conversion.
 *
 * @module recipeParser
 */

import YAML from 'yaml';
import { marked } from 'marked';

/**
 * Extracts YAML frontmatter and body content from a Markdown string.
 * Replaces gray-matter with a browser-compatible implementation.
 *
 * @param {string} content - The full Markdown file content
 * @returns {{ data: Object, content: string }} Parsed frontmatter data and body content
 * @throws {Error} If YAML parsing fails
 */
function parseFrontmatter(content) {
  const trimmed = content.trimStart();
  if (!trimmed.startsWith('---')) {
    return { data: {}, content: content };
  }

  // Find the closing --- delimiter (must be on its own line)
  const endIndex = trimmed.indexOf('\n---', 3);
  if (endIndex === -1) {
    return { data: {}, content: content };
  }

  const yamlStr = trimmed.slice(4, endIndex);
  const body = trimmed.slice(endIndex + 4);

  // Parse YAML - will throw on malformed YAML
  const data = YAML.parse(yamlStr);

  if (!data || typeof data !== 'object') {
    return { data: {}, content: body };
  }

  return { data, content: body };
}

/**
 * @typedef {Object} RecipeMetadata
 * @property {string} title
 * @property {string} category
 * @property {string} prepTime - Human-readable duration, max 50 chars
 * @property {string} cookTime - Human-readable duration, max 50 chars
 * @property {number} servings - Integer 1-999
 * @property {'easy'|'medium'|'hard'} difficulty
 * @property {string} description
 * @property {string} image - Path to recipe image
 * @property {string[]} [tags] - Optional, max 10 items
 * @property {string} [source] - Optional attribution
 * @property {string} [dateAdded] - Optional ISO 8601 date (YYYY-MM-DD)
 */

/**
 * @typedef {Object} ValidationError
 * @property {'missing_fields'|'invalid_field'|'malformed_yaml'} type
 * @property {string[]} [fields] - Names of problematic fields
 * @property {string} [message] - Human-readable error description
 */

/**
 * @typedef {Object} ParsedRecipe
 * @property {RecipeMetadata} metadata
 * @property {string} htmlContent - Sanitized HTML from Markdown body
 * @property {string} slug - Filename without .md extension
 * @property {string} category - Parent folder name
 */

/**
 * @typedef {Object} RecipeIndexEntry
 * @property {string} title
 * @property {string} slug
 * @property {string} category
 * @property {string} description
 * @property {'easy'|'medium'|'hard'} difficulty
 * @property {string} prepTime
 * @property {string} cookTime
 */

const REQUIRED_FIELDS = [
  'title',
  'category',
  'prepTime',
  'cookTime',
  'servings',
  'difficulty',
  'description',
  'image'
];

const VALID_DIFFICULTIES = ['easy', 'medium', 'hard'];

/**
 * Validates that a frontmatter object contains all required fields with non-whitespace values,
 * and that field values meet their specific constraints.
 *
 * @param {Object} frontmatterObj - The parsed frontmatter object
 * @returns {ValidationError|null} Validation error or null if valid
 */
export function validateFrontmatter(frontmatterObj) {
  if (!frontmatterObj || typeof frontmatterObj !== 'object') {
    return { type: 'missing_fields', fields: [...REQUIRED_FIELDS] };
  }

  // Check for missing or whitespace-only required fields
  const missingFields = REQUIRED_FIELDS.filter((field) => {
    const value = frontmatterObj[field];
    if (value === undefined || value === null) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  });

  if (missingFields.length > 0) {
    return { type: 'missing_fields', fields: missingFields };
  }

  // Validate prepTime: non-empty string, max 50 chars
  if (typeof frontmatterObj.prepTime !== 'string' || frontmatterObj.prepTime.length > 50) {
    return {
      type: 'invalid_field',
      fields: ['prepTime'],
      message: 'prepTime must be a non-empty string of at most 50 characters'
    };
  }

  // Validate cookTime: non-empty string, max 50 chars
  if (typeof frontmatterObj.cookTime !== 'string' || frontmatterObj.cookTime.length > 50) {
    return {
      type: 'invalid_field',
      fields: ['cookTime'],
      message: 'cookTime must be a non-empty string of at most 50 characters'
    };
  }

  // Validate servings: positive integer 1-999
  const servings = frontmatterObj.servings;
  if (
    typeof servings !== 'number' ||
    !Number.isInteger(servings) ||
    servings < 1 ||
    servings > 999
  ) {
    return {
      type: 'invalid_field',
      fields: ['servings'],
      message: 'servings must be a positive integer between 1 and 999'
    };
  }

  // Validate difficulty: case-sensitive match
  if (!VALID_DIFFICULTIES.includes(frontmatterObj.difficulty)) {
    return {
      type: 'invalid_field',
      fields: ['difficulty'],
      message: 'Must be easy, medium, or hard'
    };
  }

  return null;
}

/**
 * Sanitizes HTML by removing script tags, event handler attributes,
 * and object/embed/iframe elements.
 *
 * @param {string} htmlString - The HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return '';

  let sanitized = htmlString;

  // Remove <script> tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  // Remove self-closing or unclosed script tags
  sanitized = sanitized.replace(/<script\b[^>]*\/?>/gi, '');

  // Remove <object> tags and their content
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '');
  sanitized = sanitized.replace(/<object\b[^>]*\/?>/gi, '');

  // Remove <embed> tags
  sanitized = sanitized.replace(/<embed\b[^>]*\/?>/gi, '');
  sanitized = sanitized.replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');

  // Remove <iframe> tags and their content
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  sanitized = sanitized.replace(/<iframe\b[^>]*\/?>/gi, '');

  // Remove event handler attributes (on*)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');

  return sanitized;
}

/**
 * Formats a category folder name into a display name.
 * Replaces hyphens and underscores with spaces and capitalizes the first letter of each word.
 *
 * @param {string} folderName - The folder name (lowercase letters, numbers, hyphens, underscores)
 * @returns {string} Formatted display name
 */
export function formatCategoryName(folderName) {
  if (!folderName || typeof folderName !== 'string') return '';

  return folderName
    .replace(/[-_]/g, ' ')
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

/**
 * Truncates a description to a maximum length, appending "..." if truncated.
 *
 * @param {string} description - The description string
 * @param {number} maxLength - Maximum character length before truncation
 * @returns {string} Original string if ≤ maxLength, otherwise first maxLength chars + "..."
 */
export function truncateDescription(description, maxLength) {
  if (!description || typeof description !== 'string') return '';
  if (description.length <= maxLength) return description;
  return description.slice(0, maxLength) + '...';
}

/**
 * Parses a recipe Markdown file, extracting frontmatter and converting body to sanitized HTML.
 *
 * @param {string} markdownContent - The full Markdown file content including frontmatter
 * @param {string} filename - The filename (e.g., "sunday-gravy.md")
 * @param {string} category - The parent folder/category name
 * @returns {ParsedRecipe|ValidationError} Parsed recipe object or validation error
 */
export function parseRecipeFile(markdownContent, filename, category) {
  if (!markdownContent || typeof markdownContent !== 'string') {
    return { type: 'malformed_yaml', message: 'No content provided' };
  }

  let parsed;
  try {
    parsed = parseFrontmatter(markdownContent);
  } catch (e) {
    return { type: 'malformed_yaml', message: 'Frontmatter could not be parsed' };
  }

  // If no data was extracted (empty or invalid frontmatter)
  if (!parsed.data || Object.keys(parsed.data).length === 0) {
    // Check if the content had frontmatter delimiters but invalid YAML
    const hasFrontmatterDelimiters = markdownContent.trimStart().startsWith('---');
    if (hasFrontmatterDelimiters) {
      return { type: 'malformed_yaml', message: 'Frontmatter could not be parsed' };
    }
    return { type: 'missing_fields', fields: [...REQUIRED_FIELDS] };
  }

  const validationError = validateFrontmatter(parsed.data);
  if (validationError) {
    return validationError;
  }

  // Convert Markdown body to HTML
  const rawHtml = marked(parsed.content || '');
  const htmlContent = sanitizeHtml(rawHtml);

  // Derive slug from filename (remove .md extension)
  const slug = filename.replace(/\.md$/, '');

  return {
    metadata: { ...parsed.data },
    htmlContent,
    slug,
    category
  };
}

/**
 * Builds a recipe index from an array of parsed recipe files.
 * Groups recipes by category, sorts categories alphabetically (case-insensitive),
 * and sorts recipes within each category alphabetically by title.
 * Only includes categories that have at least one valid recipe.
 * Limits to one level of nesting.
 *
 * @param {Array<{content: string, filename: string, category: string}>} recipeFiles - Array of recipe file objects
 * @returns {{categories: Array<{name: string, displayName: string, recipes: RecipeIndexEntry[]}>}} The recipe index
 */
export function buildRecipeIndex(recipeFiles) {
  if (!Array.isArray(recipeFiles)) {
    return { categories: [] };
  }

  const categoryMap = {};

  for (const file of recipeFiles) {
    if (!file || !file.content || !file.filename || !file.category) continue;

    // Validate category folder name: lowercase letters, numbers, hyphens, underscores, max 64 chars
    const categoryPattern = /^[a-z0-9_-]+$/;
    if (
      !categoryPattern.test(file.category) ||
      file.category.length > 64
    ) {
      continue;
    }

    // Only allow one level of nesting (no slashes in category name)
    if (file.category.includes('/')) continue;

    const result = parseRecipeFile(file.content, file.filename, file.category);

    // Skip invalid recipes (those that return validation errors)
    if (result.type) continue;

    const entry = {
      title: result.metadata.title,
      slug: result.slug,
      category: result.category,
      description: result.metadata.description,
      difficulty: result.metadata.difficulty,
      prepTime: result.metadata.prepTime,
      cookTime: result.metadata.cookTime
    };

    if (!categoryMap[file.category]) {
      categoryMap[file.category] = [];
    }
    categoryMap[file.category].push(entry);
  }

  // Build sorted categories array
  const categories = Object.keys(categoryMap)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((name) => ({
      name,
      displayName: formatCategoryName(name),
      recipes: categoryMap[name].sort((a, b) =>
        a.title.localeCompare(b.title)
      )
    }));

  return { categories };
}

/**
 * Resolves a recipe by its category folder name and slug from the index.
 *
 * @param {string} category - The category folder name
 * @param {string} slug - The recipe slug (filename without .md)
 * @param {{categories: Array<{name: string, recipes: Array}>}} index - The recipe index
 * @returns {RecipeIndexEntry|null} The matching recipe entry or null
 */
export function resolveRecipeByRoute(category, slug, index) {
  if (!index || !index.categories || !category || !slug) return null;

  const categoryEntry = index.categories.find((cat) => cat.name === category);
  if (!categoryEntry) return null;

  const recipe = categoryEntry.recipes.find((r) => r.slug === slug);
  return recipe || null;
}
